// apps/web/src/pages/ProfilePage.tsx
// Phase 5 — 个人中心

import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "@stores/authStore";
import { getStatsApi } from "@api/search";
import toast from "react-hot-toast";
import { useState } from "react";

export default function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const { data: stats } = useQuery({
    queryKey: ["stats"],
    queryFn: getStatsApi,
    staleTime: 30_000,
  });

  const handleLogout = () => {
    logout();
    toast.success("已退出登录");
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-surface pb-20">
      {/* 用户信息卡 */}
      <div className="bg-gradient-to-br from-primary to-accent px-4 pt-safe pb-8 text-white">
        <div className="flex items-center gap-4">
          {/* 头像 */}
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold backdrop-blur-sm">
            {(user?.nickname || user?.email || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate">
              {user?.nickname || user?.email?.split("@")[0] || "用户"}
            </h1>
            <p className="text-sm opacity-80 truncate">{user?.email}</p>
          </div>
        </div>

        {/* 统计 */}
        <div className="grid grid-cols-4 gap-2 mt-6">
          <StatItem label="题库" value={stats?.total_banks ?? 0} />
          <StatItem label="题目" value={stats?.total_questions ?? 0} />
          <StatItem label="错题" value={stats?.wrong_count ?? 0} />
          <StatItem label="笔记" value={stats?.note_count ?? 0} />
        </div>
      </div>

      {/* 功能入口 */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow-card divide-y divide-gray-50">
          <MenuItem
            icon={<WrongIcon />}
            label="错题本"
            value={stats?.wrong_count}
            to="/wrong"
            color="text-red-500"
          />
          <MenuItem
            icon={<StarIcon />}
            label="我的收藏"
            value={stats?.favorite_count}
            to="/favorites"
            color="text-yellow-500"
          />
          <MenuItem
            icon={<NoteIcon />}
            label="我的笔记"
            value={stats?.note_count}
            to="/notes"
            color="text-green-500"
          />
        </div>
      </div>

      {/* 设置入口 */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow-card divide-y divide-gray-50">
          <MenuItem icon={<SearchIcon />} label="搜索题目" to="/search" color="text-blue-500" />
          <MenuItem icon={<ImportIcon />} label="AI 导题" to="/import" color="text-purple-500" />
        </div>
      </div>

      {/* 退出登录 */}
      <div className="px-4 mt-6">
        <button
          onClick={() => setShowLogoutConfirm(true)}
          className="w-full py-3 bg-white text-danger font-medium rounded-xl shadow-card
                     hover:bg-red-50 transition-colors btn-tap"
        >
          退出登录
        </button>
      </div>

      <p className="text-center text-xs text-gray-400 mt-4">Exam-Fast v1.0.0</p>

      {/* 退出确认弹窗 */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-base font-bold text-gray-900 mb-2">退出登录？</h3>
            <p className="text-sm text-gray-500 mb-4">
              退出后需要重新登录才能继续使用。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium btn-tap"
              >
                取消
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-2.5 bg-danger text-white rounded-xl text-sm font-medium btn-tap"
              >
                确认退出
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== 组件 ==========

function StatItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-center">
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs opacity-70 mt-0.5">{label}</p>
    </div>
  );
}

function MenuItem({
  icon,
  label,
  value,
  to,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value?: number;
  to: string;
  color: string;
}) {
  return (
    <Link to={to} className="flex items-center gap-3 px-4 py-3.5 btn-tap">
      <div className={`${color}`}>{icon}</div>
      <span className="flex-1 text-sm text-gray-700">{label}</span>
      {value !== undefined && (
        <span className="text-xs text-gray-400">{value}</span>
      )}
      <svg viewBox="0 0 24 24" className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </Link>
  );
}

// ========== 图标 ==========

function WrongIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ImportIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
